<template>
  <div>
    <EditorFormPanel
      v-model="reviewsOpen"
      :title="getEditorTranslation('text-label')"
      data-testid="customer-review-text"
    >
      <div data-testid="customer-review-form">
        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel>{{ getEditorTranslation('title-label') }}</UiFormLabel>
          </div>
          <label>
            <SfInput v-model="customerReview.text.title" type="text" data-testid="input-title">
              <template #suffix>
                <label for="text-title" class="rounded-lg cursor-pointer">
                  <input id="text-title" v-model="customerReview.text.title" type="text" class="invisible w-8" />
                </label>
              </template>
            </SfInput>
          </label>
        </div>
      </div>
    </EditorFormPanel>
    <EditorFormPanel
      v-model="layoutOpen"
      :title="getEditorTranslation('layout-group-label')"
      data-testid="customer-review-layout"
    >
      <div class="py-2 flex items-center justify-between gap-3">
        <UiFormLabel for="display-collapsible" class="m-0">
          {{ getEditorTranslation('display-collapsible-label') }}
        </UiFormLabel>

        <SfSwitch
          id="display-collapsible"
          v-model="isCollapsible"
          data-testid="display-collapsible"
          class="checked:bg-editor-button checked:before:hover:bg-editor-button checked:border-gray-500 checked:hover:border:bg-gray-700 hover:border-gray-700 hover:before:bg-gray-700 checked:hover:bg-gray-300 checked:hover:border-gray-400"
        />
      </div>

      <div class="py-2 flex items-center justify-between gap-3" :class="{ 'opacity-50': !isCollapsible }">
        <UiFormLabel for="initially-collapsed" class="m-0">
          {{ getEditorTranslation('initially-collapsed-label') }}
        </UiFormLabel>

        <SfSwitch
          id="initially-collapsed"
          v-model="isInitiallyCollapsed"
          :disabled="!isCollapsible"
          data-testid="initially-collapsed"
          class="checked:bg-editor-button checked:before:hover:bg-editor-button checked:border-gray-500 checked:hover:border:bg-gray-700 hover:border-gray-700 hover:before:bg-gray-700 checked:hover:bg-gray-300 checked:hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      <EditorFullWidthToggle v-model="isFullWidth" :block-uuid="blockUuid" />

      <div class="py-2">
        <UiFormLabel>{{ getEditorTranslation('padding-label') }}</UiFormLabel>
        <div class="grid grid-cols-4 gap-px rounded-md overflow-hidden border border-gray-300">
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowUpward /></span>
            <input
              v-model.number="customerReview.layout.paddingTop"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-top"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowDownward /></span>
            <input
              v-model.number="customerReview.layout.paddingBottom"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-bottom"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowBack /></span>
            <input
              v-model.number="customerReview.layout.paddingLeft"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-left"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white">
            <span><SfIconArrowForward /></span>
            <input
              v-model.number="customerReview.layout.paddingRight"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-right"
            />
          </div>
        </div>
      </div>
    </EditorFormPanel>
  </div>
</template>

<script setup lang="ts">
import type { CustomerReviewContent } from './types';
import {
  SfInput,
  SfIconArrowUpward,
  SfIconArrowDownward,
  SfIconArrowBack,
  SfIconArrowForward,
  SfSwitch,
} from '@storefront-ui/vue';

const props = defineProps<{ uuid?: string }>();

const reviewsOpen = ref(true);
const layoutOpen = ref(true);
const { findOrDeleteBlockByUuid } = useBlockManager();
const { blockUuid } = useSiteConfiguration();
const { allBlocks: data } = useBlocks();

const customerReview = computed<CustomerReviewContent>(() => {
  const uuid = props.uuid || blockUuid.value;
  const rawContent = findOrDeleteBlockByUuid(data.value, uuid)?.content ?? {};
  const content = rawContent as Partial<CustomerReviewContent>;

  if (!content.text) {
    content.text = { title: '' };
  }

  if (!content.layout) {
    content.layout = {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      collapsible: true,
      initiallyCollapsed: true,
      fullWidth: false,
    };
  } else {
    if (content.layout.paddingTop === undefined) content.layout.paddingTop = 0;
    if (content.layout.paddingBottom === undefined) content.layout.paddingBottom = 0;
    if (content.layout.paddingLeft === undefined) content.layout.paddingLeft = 0;
    if (content.layout.paddingRight === undefined) content.layout.paddingRight = 0;
    if (content.layout.collapsible === undefined) content.layout.collapsible = true;
    if (content.layout.initiallyCollapsed === undefined) content.layout.initiallyCollapsed = true;
    if (content.layout.fullWidth === undefined) content.layout.fullWidth = false;
  }

  return content as CustomerReviewContent;
});

const { isFullWidth } = useFullWidthToggleForContent(customerReview);

const isCollapsibleInit = customerReview.value.layout.collapsible;
const isCollapsible = ref(isCollapsibleInit);

watch(isCollapsible, (newValue) => {
  customerReview.value.layout.collapsible = newValue;

  if (!newValue) {
    isInitiallyCollapsed.value = false;
    customerReview.value.layout.initiallyCollapsed = false;
  }
});

const isInitiallyCollapsedInit = customerReview.value.layout.initiallyCollapsed;
const isInitiallyCollapsed = ref(isInitiallyCollapsedInit);

watch(isInitiallyCollapsed, (newValue) => {
  customerReview.value.layout.initiallyCollapsed = newValue;
});
</script>

<i18n lang="json">
{
  "en": {
    "text-label": "Text",
    "title-label": "Title",
    "padding-label": "Padding",
    "display-collapsible-label": "Display as collapsible",
    "initially-collapsed-label": "Initially collapsed",
    "layout-group-label": "Layout Settings"
  },
  "de": {
    "text-label": "Text",
    "title-label": "Titel",
    "padding-label": "Padding",
    "display-collapsible-label": "Als zusammenklappbar anzeigen",
    "initially-collapsed-label": "Ursprünglich kollabiert",
    "layout-group-label": "Layout Settings"
  }
}
</i18n>
